import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
} from "react-router-dom";
import Panel from "@main/panel-components/Panel";
import Login from "@beeldit/user-and-access/auth/pages/Login";
import { AuthProvider } from "@beeldit/user-and-access/auth/AuthContext";

import { TenantAndCommerceProvider } from "./contexts/TenantAndCommerceContext";

import { ConfigurationsProvider } from "./contexts/ConfigurationsContext";

import { User } from "@beeldit/user-and-access/users/models/User";
import { Commerce } from "./commerces/models/Commerce";

import { EditActionProvider as UserEditActionProvider } from "@beeldit/user-and-access/users/actions/edit/EditActionContext";
import { ListActionProvider as UserListActionProvider } from "@beeldit/user-and-access/users/actions/list/ListActionContext";
import { CreateActionProvider as UserCreateActionProvider } from "@beeldit/user-and-access/users/actions/create/CreateActionContext";
import { RemoveActionProvider as UserRemoveActionProvider } from "@beeldit/user-and-access/users/actions/remove/RemoveActionContext";
import UserPage from "@beeldit/user-and-access/users/pages/UserPage";

import { ListActionProvider as UsersActivityListActionProvider } from "@beeldit/user-and-access/activity/actions/list/ListActionContext";
import { DetailsActionProvider as UsersActivityDetailsActionProvider } from "@beeldit/user-and-access/activity/actions/details/DetailsActionContext";
import UsersActivityPage from "@beeldit/user-and-access/activity/pages/UsersActivityPage";
import ConfigurationPage from "./configurations/pages/ConfigurationPage";
import UpdatesPage from "./updates/pages/UpdatesPage";
import IntegrationsPage from "./integrations/pages/IntegrationsPage";
import CustomersPage from "./customers/pages/CustomersPage";
import CustomerPage from "./customers/pages/CustomerPage";
import { DetailsActionProvider } from "@beeldit/core/actions/details/DetailsActionContext";
import TreatmentsPage from "./treatments/pages/TreatmentsPage";
import TreatmentSessionTypesPage from "./treatment-session-types/pages/TreatmentSessionTypesPage";
import TherapistsPage from "./therapists/pages/TherapistsPage";
import SessionsCalendarPage from "./sessions/pages/SessionsCalendarPage";
import CustomerPanel from "./panel-components/CustomerPanel";
import LastSessionCard from "./panel-components/LastSessionCard";
import NextSessionsCards from "./panel-components/NextSessionsCards";
import ContactForm from "./panel-components/ContactForm";
import { SelectedCustomerProvider } from "./contexts/SelectedCustomerContext";
import RequestResetPassword from "@beeldit/user-and-access/auth/pages/RequestResetPassword";
import ResetPassword from "@beeldit/user-and-access/auth/pages/ResetPassword";
import AccountConfirmation from "@beeldit/user-and-access/auth/pages/AccountConfirmation";
import SchedulesPage from "./schedules/pages/SchedulesPage";
import { DetailsByCustomerActionProvider } from "./customer-histories/actions/details/DetailsByCustomerActionContext";
import BDConnectionsPage from "./bd-connections/pages/BDConnectionsPage";
import CustomReportsPage from "./custom-reports/pages/CustomReportsPage";
import CustomReportPage from "./custom-reports/pages/CustomReportPage";
import AccountingPage from "./accounting/pages/AccountingPage";

const redirectFunction = (user: User) => {
  let extraData = user.extra_data;
  let availableTenants = extraData.tenants;
  let selectedTenant = availableTenants[0];
  if (!selectedTenant) {
    return "/panel";
  }
  let selectedTenantId = selectedTenant.id;
  let availableCommerces = extraData.commerces;
  let selectedCommerce = availableCommerces.find(
    (commerce: Commerce) => (commerce.tenant_id = selectedTenantId)
  );
  let selectedCommerceId = selectedCommerce.id;
  return `/panel/${selectedTenantId}/${selectedCommerceId}`;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <AuthProvider>
        <ConfigurationsProvider>
          <div className="App">
            <Outlet />
          </div>
        </ConfigurationsProvider>
      </AuthProvider>
    ),
    children: [
      {
        path: "/login",
        element: (
          <Login
            redirectFunction={(user: User) => {
              return redirectFunction(user);
            }}
          />
        ),
      },
      {
        path: "/account-confirmation",
        element: <AccountConfirmation />,
      },
      {
        path: "/resetPassword",
        element: <ResetPassword />,
      },
      {
        path: "/request-reset-password",
        element: <RequestResetPassword />,
      },
      {
        path: "/",
        index: true,
        element: <Navigate to="/panel/1/1" />,
      },
      {
        path: "/panel",
        index: true,
        element: <Navigate to="/panel/1/1" />,
      },
      {
        path: "/panel/:tenantId/:commerceId",
        element: (
          <TenantAndCommerceProvider>
            <Panel />
          </TenantAndCommerceProvider>
        ),
        children: [
          {
            path: "customers",
            element: <CustomersPage />,
          },
          {
            path: "customers/:customerId",
            element: (
              <DetailsActionProvider>
                <DetailsByCustomerActionProvider>
                  <CustomerPage />
                </DetailsByCustomerActionProvider>
              </DetailsActionProvider>
            ),
          },
          {
            path: "treatments",
            element: <TreatmentsPage />,
          },
          {
            path: "treatment-session-types/:treatmentId",
            element: <TreatmentSessionTypesPage />,
          },
          {
            path: "schedules/:customerTreatmentId",
            element: <SchedulesPage />,
          },
          {
            path: "therapists",
            element: <TherapistsPage />,
          },
          {
            path: "sessions",
            element: <SessionsCalendarPage />,
          },
          {
            path: "accounting",
            element: <AccountingPage />,
          },
          {
            path: "configurations",
            children: [
              {
                path: "users",
                element: (
                  <UserListActionProvider>
                    <UserRemoveActionProvider>
                      <UserCreateActionProvider>
                        <UserEditActionProvider>
                          <UserPage />
                        </UserEditActionProvider>
                      </UserCreateActionProvider>
                    </UserRemoveActionProvider>
                  </UserListActionProvider>
                ),
              },
              {
                path: "users-activity",
                element: (
                  <UsersActivityListActionProvider>
                    <UsersActivityDetailsActionProvider>
                      <UsersActivityPage />
                    </UsersActivityDetailsActionProvider>
                  </UsersActivityListActionProvider>
                ),
              },
              {
                path: "configuration",
                element: <ConfigurationPage></ConfigurationPage>,
              },
              {
                path: "reports",
                element: <CustomReportsPage></CustomReportsPage>,
              },
              {
                path: "reports/:reportId",
                element: <CustomReportPage></CustomReportPage>,
              },
              {
                path: "updates",
                element: <UpdatesPage />,
              },
              {
                path: "integrations",
                element: <IntegrationsPage />,
              },
              {
                path: "bd-connections",
                element: <BDConnectionsPage />,
              },
            ],
          },
        ],
      },
      {
        path: "/customer-panel/:tenantId/:commerceId",
        element: (
          <TenantAndCommerceProvider>
            <SelectedCustomerProvider>
              <CustomerPanel />
            </SelectedCustomerProvider>
          </TenantAndCommerceProvider>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="future-sessions" replace />,
          },
          {
            path: "home",
            element: <Navigate to="future-sessions" replace />,
          },
          {
            path: "future-sessions",
            element: <NextSessionsCards />,
          },
          {
            path: "last-sessions",
            element: <LastSessionCard />,
          },
          {
            path: "contact",
            element: <ContactForm />,
          },
        ],
      },
    ],
  },
]);

export function Router() {
  return <RouterProvider router={router} />;
}
